import { TranslationDictionary } from '../types';

// This will allow us to access the translation instance in other files
declare global {
  interface Window {
    virtenzoTranslation?: VirtenzoTranslation;
  }
}

/**
 * Translation utility for the configurator
 * Handles language switching and text translation
 */
class VirtenzoTranslation {
  private translations: TranslationDictionary;
  private currentLanguage: string;
  
  constructor() {
    this.translations = {
      en: {
        // Section headers
        'virtenzo.configurator.section_wood': 'Wood',
        'virtenzo.configurator.section_upholstery': 'Upholstery',
        'virtenzo.configurator.section_upholstery_backrest': 'Backrest Upholstery',
        'virtenzo.configurator.section_upholstery_seat': 'Seat Upholstery',
        'virtenzo.configurator.section_configurations': 'Configurations',
        
        'virtenzo.configuration': 'Configuration',
        'virtenzo.configurations': 'Configurations',

        // Wood types
        'virtenzo.configurator.beech': 'Beech',
        'virtenzo.configurator.oak': 'Oak',
        'virtenzo.configurator.american_walnut': 'American Walnut',
        
        // Fabric categories
        'virtenzo.configurator.fabrics_group_a': 'Fabrics - Group A',
        'virtenzo.configurator.fabrics_group_b': 'Fabrics - Group B',
        'virtenzo.configurator.fabrics_group_c': 'Fabrics - Group C',
        'virtenzo.configurator.leather': 'Leather',
        
        // Form values
        'virtenzo.form.input_value_group_a': 'Group A',
        'virtenzo.form.input_value_group_b': 'Group B',
        'virtenzo.form.input_value_group_c': 'Group C',
        'virtenzo.form.input_value_leather': 'Leather',
        
        'virtenzo.form.input_value_beech': 'Beech',
        'virtenzo.form.input_value_oak': 'Oak',
        'virtenzo.form.input_value_american_walnut': 'American Walnut',
        
        'virtenzo.form.input_name_wood': 'Wood type',
        'virtenzo.form.input_name_upholstery': 'Upholstery',
        
        // Format strings
        'virtenzo.wood_label_format_default': '{wood}',
        'virtenzo.upholstery_label_format_default': '{upholstery}',
        'virtenzo.upholstery_label_format_backrest': 'Backrest: {upholstery}',
        'virtenzo.upholstery_label_format_seat': 'Seat: {upholstery}',
        
        // Configurator labels
        'virtenzo.configurator.products': 'Products',
        'virtenzo.configurator.search': 'Search',
        'virtenzo.configurator.loading': 'Loading model...',
        'virtenzo.configurator.share': 'Share configurator URL',
        'virtenzo.configurator.url_copied': 'Configurator URL copied to clipboard!'
      },
      // Add more languages as needed
      pl: {
        'virtenzo.configurator.section_wood': 'Drewno',
        'virtenzo.configurator.section_upholstery': 'Tapicerka',
        'virtenzo.configurator.section_upholstery_backrest': 'Tapicerka oparcia',
        'virtenzo.configurator.section_upholstery_seat': 'Tapicerka siedziska',
        'virtenzo.configurator.section_configurations': 'Konfiguracje',

        'virtenzo.configuration': 'Konfiguracja',
        'virtenzo.configurations': 'Konfiguracje',

        'virtenzo.configurator.beech': 'Buk',
        'virtenzo.configurator.oak': 'Dąb',
        'virtenzo.configurator.american_walnut': 'Orzech amerykański',
        
        'virtenzo.configurator.fabrics_group_a': 'Tkaniny - Grupa A',
        'virtenzo.configurator.fabrics_group_b': 'Tkaniny - Grupa B',
        'virtenzo.configurator.fabrics_group_c': 'Tkaniny - Grupa C',
        'virtenzo.configurator.leather': 'Skóra',
        
        'virtenzo.form.input_value_group_a': 'Grupa A',
        'virtenzo.form.input_value_group_b': 'Grupa B',
        'virtenzo.form.input_value_group_c': 'Grupa C',
        'virtenzo.form.input_value_leather': 'Skóra',
        
        'virtenzo.form.input_value_beech': 'Buk',
        'virtenzo.form.input_value_oak': 'Dąb',
        'virtenzo.form.input_value_american_walnut': 'Orzech amerykański',
        
        'virtenzo.form.input_name_wood': 'Rodzaj drewna',
        'virtenzo.form.input_name_upholstery': 'Tapicerka',
        
        'virtenzo.wood_label_format_default': '{wood}',
        'virtenzo.upholstery_label_format_default': '{upholstery}',
        'virtenzo.upholstery_label_format_backrest': 'Oparcie: {upholstery}',
        'virtenzo.upholstery_label_format_seat': 'Siedzisko: {upholstery}',

        'virtenzo.configurator.products': 'Produkty',
        'virtenzo.configurator.search': 'Szukaj',
        'virtenzo.configurator.loading': 'Ładowanie modelu...',
        'virtenzo.configurator.share': 'Udostępnij URL konfiguratora',
        'virtenzo.configurator.url_copied': 'URL konfiguratora skopiowany do schowka!'
      }
    };
    
    this.currentLanguage = 'en'; // Default language
  }
  
  /**
   * Set the current language for translations
   * @param language Language code (e.g., 'en', 'pl')
   */
  setLanguage(language: string): void {
    if (this.translations[language]) {
      this.currentLanguage = language;
    } else {
      console.warn(`Language '${language}' not supported, falling back to English`);
      this.currentLanguage = 'en';
    }
  }
  
  /**
   * Translate a key to the current language
   * @param key Translation key
   * @returns Translated text or the key itself if translation not found
   */
  t(key: string): string {
    if (this.translations[this.currentLanguage] && 
        this.translations[this.currentLanguage][key]) {
      return this.translations[this.currentLanguage][key];
    }
    
    // Fallback to English if the current language doesn't have the translation
    if (this.translations['en'] && this.translations['en'][key]) {
      return this.translations['en'][key];
    }
    
    // Return the key if no translation found
    return key;
  }
  
  /**
   * Get the current language
   * @returns Current language code
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }
  
  /**
   * Add or update translations for a specific language
   * @param language Language code
   * @param translations Object with key-value pairs of translations
   */
  addTranslations(language: string, translations: Record<string, string>): void {
    if (!this.translations[language]) {
      this.translations[language] = {};
    }
    
    this.translations[language] = {
      ...this.translations[language],
      ...translations
    };
  }
}

// Create a singleton instance
const translationInstance = new VirtenzoTranslation();

// Make the instance globally available
if (typeof window !== 'undefined') {
  window.virtenzoTranslation = translationInstance;
}

export default translationInstance;
